import {
  Divider,
  LinearProgress,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { getLoanTestCases } from '@src/api/testcase-api'
import { reportErrorToConsole } from '@src/services/error-logger'
import { every } from 'lodash-es'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../Common/Dialog/FormDialog'
import { LoanSchemaType } from './loan-form-schema'

export interface LoanTestCase {
  id: string
  loanData: LoanSchemaType
  description: string
}

interface DialogProps {
  onTestCaseSelected: (loanData: LoanSchemaType) => void
  open: boolean
  onClose: () => void
}

interface TestCaseItemProps {
  onTestCaseSelected: (loanData: LoanSchemaType) => void
  testcase: LoanTestCase
  selected: boolean
}

const TestCaseItem = ({ onTestCaseSelected, testcase, selected }: TestCaseItemProps) => {
  const { loanData } = testcase

  const primaryText = `${loanData.financingProgramId} ${loanData.contractNumber}` // Should change to something better
  return (
    <>
      <ListItemButton onClick={() => onTestCaseSelected(testcase.loanData)} selected={selected}>
        <ListItemText primary={primaryText} secondary={testcase.description} />
      </ListItemButton>
      <Divider />
    </>
  )
}

const ListLoanTestCaseDialog = ({ open, onClose, onTestCaseSelected }: DialogProps) => {
  const { t } = useTranslation()

  const [selectedLoan, setSelectedLoan] = useState<LoanSchemaType | null>(null)
  const [loanTestCases, setLoanTestCases] = useState<LoanTestCase[]>([])
  const [filteredLoanTestCases, setFilteredLoanTestCases] = useState<LoanTestCase[]>([])
  const [textFilter, setTextFilter] = useState('')
  const [isLoading, setLoading] = useState(false)

  useEffect(() => {
    if (open && loanTestCases.length === 0) {
      setLoading(true)
      getLoanTestCases()
        .then((values: LoanTestCase[]) => {
          setLoading(false)
          setLoanTestCases(values)
        })
        .catch(reportErrorToConsole)
    }
  }, [open, loanTestCases.length])

  useEffect(() => {
    const filters = textFilter.toLowerCase().split(' ')
    const match = loanTestCases.filter((testcase) => {
      return every(
        filters,
        (f) =>
          testcase.loanData.financingProgramId.toLowerCase().includes(f) ||
          testcase.loanData.contractNumber.toLowerCase().includes(f),
      )
    })
    setFilteredLoanTestCases(match)
  }, [textFilter, loanTestCases])

  return (
    <FormDialog
      open={open}
      maxWidth="xl"
      onClose={onClose}
      disabled={selectedLoan === null}
      dividers
      disableGutterContent
      title={
        <Stack spacing={1}>
          <Typography variant="inherit">{t('loan.testCases.title')}</Typography>
          <TextField name="filter" onChange={(e) => setTextFilter(e.target.value)} value={textFilter} label="Filtre" />
        </Stack>
      }
      onConfirm={() => {
        if (selectedLoan) onTestCaseSelected(selectedLoan)
      }}
    >
      {isLoading && <LinearProgress />}
      {!isLoading && filteredLoanTestCases.length > 0 && (
        <List>
          {filteredLoanTestCases.map((testcase) => (
            <TestCaseItem
              key={testcase.id}
              testcase={testcase}
              onTestCaseSelected={setSelectedLoan}
              selected={testcase.loanData === selectedLoan}
            />
          ))}
        </List>
      )}
      {!isLoading && filteredLoanTestCases.length === 0 && <p>{t('loan.testCases.notFound')}</p>}
    </FormDialog>
  )
}

export default ListLoanTestCaseDialog
